import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import useCollection from "../../hooks/useCollection";
import useStoreProducts from "../../hooks/useStoreProducts";
import Error500Page from "../../pages/500";
import CartStorage from "../../services/frontend/CartStorage";
import Basket from "../Cart/Basket";
import EmptyStore from "../EmptyStore";
import NavBar from "../Navbar";
import SideNavDrawer from "../SideNavDrawer";
import { Skeleton } from "../Skeleton";
import SkeletonCard from "../SkeletonCard";
import StoreCollections from "../StoreCollections";
import StoreItem from "../StoreItem";
import RestorableList from "./RestorableList";
import "resize-observer-polyfill";

export const ALL_PRODUCTS_OBJECT = {
  name: "all products",
  _id: "all",
};

const CollectionHome = ({ store, theme }) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const { collection: queryCollection } = router.query;
  const [selectedCollection, setSelectedCollection] = useState(ALL_PRODUCTS_OBJECT);
  const [lastEl, setLastEl] = useState(null);
  const { ref, inView } = useInView({
    threshold: 0,
  });

  const {
    data: productData,
    isLoading: isProductsLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error: productError,
  } = useStoreProducts(store.id, selectedCollection?._id);

  const {
    data: collectionsData,
    isLoading: isCollectionsLoading,
    error: collectionsError,
  } = useCollection(store.id, "");

  // Assuming collectionsData is now structured as { collections: [...] }
  const collections = collectionsData ? collectionsData.collections : [];
  //console.log("shubham", collectionsData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const products = productData?.pages.flatMap((page) => page.products) || [];
  const [cart, setCart] = useState(CartStorage.getInitialCart());
  const [showCart, setShowCart] = useState(false);
  const [showSideNav, setShowSideNav] = useState(false);
  const [isCartInitialized, setIsCartInitialized] = useState(false);

  // Initialize cart from CartStorage when component mounts
  useEffect(() => {
    const initialize = async () => {
      const initialCartData = await CartStorage.initializeCart();
      setCart(initialCartData);
      setIsCartInitialized(true);
    };
    initialize();
  }, []);

  // Update CartStorage when cart changes
  useEffect(() => {
    if (cart !== CartStorage.getInitialCart()) {
      CartStorage.saveCart(cart);
    }
  }, [cart]);

  const handleShowCart = useCallback(
    (boolVal = false) => {
      if (isCollectionsLoading || collectionsError || productError) return;
      setShowCart(boolVal);
    },
    [isCollectionsLoading, collectionsError, productError],
  );

  useEffect(() => {
    if (queryCollection) {
      const foundCollection = collections.find((c) => {
        return c.name.trim().toLowerCase() === queryCollection.trim().toLowerCase();
      });
      setSelectedCollection(foundCollection);
    }
  }, [collections]);

  const setFilter = useCallback(
    (collection) => {
      queryClient.invalidateQueries(["storeProducts", store.id, collection]);
      queryClient.removeQueries(["storeProducts", store.id, selectedCollection]);
      setSelectedCollection(collection);
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            collection: collection.name === "all products" ? "" : collection.name,
          },
        },
        undefined,
        { shallow: true },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router],
  );

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (!products.length) return;
    const [lastElement] = products.slice(-1);
    setLastEl(lastElement.id);
  }, [products]);

  const updateAndSaveCart = async (newCartData) => {
    setCart(newCartData);
    await CartStorage.saveCart(newCartData);
  };

  const updateAddedToCart = (productId, value) => {
    console.log(
      `Product ${productId} added status: ${value} (update logic needed here if applicable)`,
    );
  };

  if (collectionsError || productError) return <Error500Page />;
  return (
    <div className="w-full min-h-[calc(100vh-268px)] mt-[38px] px-[22px] scrollbar-none">
      {isCartInitialized && cart?.products?.length > 0 && (
        <Basket
          isBasketOpen={showCart}
          setCart={updateAndSaveCart}
          cartData={cart}
          updateAddedToCart={updateAddedToCart}
          handleShowCart={handleShowCart}
          store={store}
          setRefresh={() => {}}
        />
      )}
      <NavBar
        cartActive={cart.products.length > 0}
        handleShowCart={handleShowCart}
        hideInAdvance={false}
        homeActive={false}
        store={store}
        theme={theme}
        storeName={store?.storeName || ""}
        toggleSideNav={() => setShowSideNav(!showSideNav)}
      />
      <SideNavDrawer
        isOpen={showSideNav}
        toggleDrawer={() => setShowSideNav(!showSideNav)}
        store={store}
      />
      <div
        className={
          isCollectionsLoading || (collections && collections.length > 0)
            ? "relative top-14 left-0 right-0 flex leading-8 mt-[5px] px-0 overflow-x-auto overflow-y-hidden max-w-[500px] h-12 scroll-smooth z-10 hide-scrollbar"
            : ""
        }
      >
        {collections && collections.length > 0 ? (
          <StoreCollections
            collections={collections}
            setFilter={setFilter}
            selectedCollection={selectedCollection}
          />
        ) : (
          <div className="h-5"></div>
        )}
        {isCollectionsLoading &&
          Array(3)
            .fill(0)
            .map((_, i) => (
              <Skeleton
                className="m-[4px_5px_5px_4px] px-5 py-[4px] rounded-[50px] w-[150px]"
                key={i}
              />
            ))}
      </div>
      <div className="grid mt-4 gap-[11px_22px] relative pt-16 min-h-[80vh]">
        {products.length > 0 ? (
          <RestorableList>
            {React.Children.toArray(
              products.map((_, index) => {
                const startIndex = index * 2;
                const endIndex = startIndex + 2;
                const rowProducts = products.slice(startIndex, endIndex);
                if (!rowProducts.length) return null;
                return (
                  <div className="grid grid-cols-2 gap-4">
                    {React.Children.toArray(
                      rowProducts.map((product) => (
                        <div ref={lastEl === product.id ? ref : null}>
                          <StoreItem
                            cohort={store.cohort.includes(2) ? store.cohort : null}
                            product={product}
                            top={index === 0}
                          />
                        </div>
                      )),
                    )}
                  </div>
                );
              }),
            )}
          </RestorableList>
        ) : (
          !isProductsLoading && <EmptyStore store={store} />
        )}
        {/* Loader for next page */}
        {(isFetchingNextPage || isProductsLoading) && (
          <div className="grid grid-cols-2 gap-4">
            {Array(6)
              .fill(0)
              .map((_, i) => (
                <div key={i}>
                  <SkeletonCard
                    imageClass="rounded-lg object-cover max-w-[none] w-[calc((100vw/2)-33px)] h-[calc((100vw/2)-33px)] md:w-[calc((520px/2)-33px)] md:h-[calc((520px/2)-33px)]"
                    titleClass="h-4 w-[calc((100vw/2)-33px)] md:w-[calc((520px/2)-33px)]"
                    descriptionClass="h-4 w-[calc((100vw/2)-150px)] md:w-[calc((520px/2)-150px)]"
                    containerClass="flex justify-start items-start flex-col max-w-[calc((100vw/2)-33px)] mb-8 md:max-w-[calc((520px/2)-33px)]"
                    key={i}
                  />
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionHome;
